/**
 * Shared admin API client.
 *
 * Eliminates the repeated fetch + headers + session.access_token pattern
 * that appears in every admin component. All admin edge function calls
 * go through this client.
 */
import type { Session } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

function headers(session: Session): Record<string, string> {
  return {
    'Authorization': `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  }
}

type QueryParams = Record<string, string | number | boolean | null | undefined>

interface RequestOptions {
  method?: string
  body?: unknown
  params?: QueryParams
  signal?: AbortSignal
}

async function request<T>(
  session: Session,
  path: string,
  options?: RequestOptions,
): Promise<T> {
  const { method = 'GET', body, params, signal } = options ?? {}
  const url = new URL(`${supabaseUrl}/functions/v1/${path}`)
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v == null || v === false || v === '') continue
      url.searchParams.set(k, String(v))
    }
  }

  const res = await fetch(url.toString(), {
    method,
    headers: headers(session),
    signal,
    ...(body ? { body: JSON.stringify(body) } : {}),
  })

  const data = await res.json()
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `Request failed: ${res.status}`)
  }
  return data as T
}

// --- Booking endpoints ---

export type GetBookingsParams = {
  price_drops?: boolean
  active_only?: boolean
  inactive_only?: boolean
  archived_only?: boolean
  user_id?: string
  limit?: number
  offset?: number
  search?: string
}

interface BookingsResponse {
  success: boolean
  bookings: unknown[]
  pagination?: { total: number }
}

export async function getBookings(session: Session, params: GetBookingsParams = {}) {
  return request<BookingsResponse>(session, 'admin-get-bookings', { params })
}

// --- Stats endpoint ---

interface StatsResponse {
  success: boolean
  stats: {
    total_bookings: number
    active_bookings: number
    archived_bookings: number
    total_users: number
    price_drops_count: number
    total_potential_savings: number
    bookings_last_7_days: number
    price_checks_last_100: number
    successful_price_checks: number
    revenue_earned?: number
    savings_delivered?: number
    success_rate?: number
  }
}

export async function getStats(session: Session) {
  return request<StatsResponse>(session, 'admin-get-stats')
}

// --- Price check ---

interface CheckPriceResponse {
  success: boolean
  result?: string
  savings?: number
  current_price?: number
  current_points?: number
  message?: string
}

export async function checkPrice(session: Session, bookingId: string) {
  return request<CheckPriceResponse>(session, 'admin-check-price', {
    method: 'POST',
    body: { booking_id: bookingId },
  })
}

// --- Rebook ---

export interface RebookParams {
  booking_id: string
  new_price: number
  original_price?: number
}

export interface RebookResponse {
  success: boolean
  booking_id: string
  new_baseline: number
  notification_sent: boolean
  commission?: {
    grossSavings: number
    commissionAmount: number
    netSavings: number
    rateApplied: number
    belowThreshold: boolean
    isAward: boolean
  }
  charge_status?: string
  error?: string
}

export async function rebook(session: Session, params: RebookParams) {
  return request<RebookResponse>(session, 'rebook', {
    method: 'POST',
    body: params,
  })
}

// --- Archive / Reactivate / Delete ---

export async function archiveBooking(session: Session, bookingId: string) {
  return request<{ success: boolean }>(session, 'admin-archive-booking', {
    method: 'POST',
    body: { booking_id: bookingId },
  })
}

export async function reactivateBooking(session: Session, bookingId: string) {
  return request<{ success: boolean }>(session, 'admin-reactivate-booking', {
    method: 'POST',
    body: { booking_id: bookingId },
  })
}

export async function deleteBooking(session: Session, bookingId: string, pnrConfirmation: string) {
  return request<{ success: boolean }>(session, 'admin-delete-booking', {
    method: 'POST',
    body: { booking_id: bookingId, pnr_confirmation: pnrConfirmation },
  })
}

// --- Billing ---

export interface BillingStats {
  revenue_earned: number
  pending_amount: number
  failed_amount: number
  savings_delivered: number
  pending_count: number
  failed_count: number
}

export interface LedgerEntry {
  id: string
  savings_event_id: string
  user_id: string
  user_email?: string
  user_name?: string
  amount: number
  currency: string
  status: string
  stripe_payment_intent_id?: string
  error_message?: string
  collected_at?: string
  created_at: string
  // From savings_events join
  original_price?: number
  new_price?: number
  route?: string
  airline_code?: string
  is_award_booking?: boolean
  booking_id?: string
}

export interface BillingResponse {
  success: boolean
  stats: BillingStats
  ledger: LedgerEntry[]
}

export async function getBilling(session: Session) {
  return request<BillingResponse>(session, 'admin-get-billing')
}

/**
 * Filtered version of getBilling for the admin user explorer. Scopes all
 * stats and ledger rows to a single user_id.
 */
export async function getBillingForUser(session: Session, userId: string) {
  return request<BillingResponse>(session, 'admin-get-billing', {
    params: { user_id: userId },
  })
}

export async function retryCharge(session: Session, ledgerEntryId: string) {
  return request<{ success: boolean }>(session, 'retry-failed-charges', {
    method: 'POST',
    body: { ledger_entry_id: ledgerEntryId },
  })
}

// --- Ops Health ---

export interface OpsProbe {
  name: string
  status: 'pass' | 'fail' | 'skip'
  latency_ms: number
  error_message?: string | null
  last_checked: string
  metadata?: Record<string, unknown> | null
}

export interface OpsHistoryEntry {
  probe_name: string
  status: 'pass' | 'fail' | 'skip'
  checked_at: string
}

export interface OpsHealthResponse {
  canary: {
    probes: OpsProbe[]
    history: OpsHistoryEntry[]
    last_run: string | null
  }
  operations: {
    last_price_check_cash: string | null
    last_price_check_award: string | null
    last_email_sent: string | null
    active_bookings: number
    errors_24h: number
  }
}

export async function getOpsHealth(session: Session) {
  return request<OpsHealthResponse>(session, 'admin-get-ops-health')
}

// --- Timeline ---

/**
 * Keep in sync with `TimelineEventType` in
 * supabase/functions/admin-get-booking-timeline/format.ts — both consumers
 * must handle the same set exhaustively. When adding a new variant:
 *   1. Update this union
 *   2. Update TimelineEventType in format.ts
 *   3. Add the icon to admin/src/components/shared/timelineIcons.ts
 */
export type TimelineEventType =
  | 'created'
  | 'price_check'
  | 'drop_detected'
  | 'error'
  | 'linear_created'
  | 'linear_resolved'
  | 'rebooked'
  | 'commission_charged'
  | 'commission_failed'
  | 'commission_waived'
  | 'commission_refunded'
  | 'email_received'

export interface TimelineEvent {
  timestamp: string
  event_type: TimelineEventType
  detail: string
  metadata?: Record<string, unknown>
  /** Unique across sources: `pc:<uuid>`, `cl:<uuid>`, etc. Used as the
   * secondary sort key to deterministically break tied timestamps during
   * composite cursor pagination. */
  source_id: string
}

export interface TimelineCursor {
  ts: string
  source_id: string
}

export interface TimelineResponse {
  success: boolean
  events: TimelineEvent[]
  next_cursor: TimelineCursor | null
}

export async function getBookingTimeline(
  session: Session,
  bookingId: string,
  cursor?: TimelineCursor | null,
) {
  const params: Record<string, string> = { booking_id: bookingId }
  if (cursor) {
    params.cursor_ts = cursor.ts
    params.cursor_id = cursor.source_id
  }
  return request<TimelineResponse>(session, 'admin-get-booking-timeline', { params })
}

// --- Linear ---

export async function resolveLinearIssue(session: Session, linearIssueId: string, bookingId: string) {
  return request<{ success: boolean }>(session, 'admin-resolve-linear-issue', {
    method: 'POST',
    body: { linear_issue_id: linearIssueId, booking_id: bookingId },
  })
}

export async function cancelLinearIssue(session: Session, linearIssueId: string, bookingId: string) {
  return request<{ success: boolean }>(session, 'admin-cancel-linear-issue', {
    method: 'POST',
    body: { linear_issue_id: linearIssueId, booking_id: bookingId },
  })
}

export async function createLinearIssueForBooking(session: Session, bookingId: string) {
  return request<{ success: boolean; issue_url?: string; issue_number?: number }>(
    session,
    'admin-create-linear-issue',
    { method: 'POST', body: { booking_id: bookingId } },
  )
}

// --- Research CRUD ---

export interface ResearchItinerary {
  id: string
  label: string
  trip_type: string
  cabin_class: string
  origin: string
  destination: string
  carrier_code: string
  flight_number: string
  departure_date: string
  departure_time?: string
  return_origin?: string
  return_destination?: string
  return_carrier_code?: string
  return_flight_number?: string
  return_date?: string
  is_active: boolean
  baseline_price?: number
  latest_price?: number
  latest_checked_at?: string
  archived_at?: string
  notes?: string
  created_at: string
}

export async function getResearchItineraries(session: Session) {
  return request<{ success: boolean; itineraries: ResearchItinerary[] }>(
    session, 'admin-research-itineraries',
  )
}

export async function createResearchItinerary(session: Session, data: Partial<ResearchItinerary>) {
  return request<{ success: boolean; itinerary: ResearchItinerary }>(
    session, 'admin-research-itineraries',
    { method: 'POST', body: data },
  )
}

export async function updateResearchItinerary(session: Session, id: string, data: Partial<ResearchItinerary>) {
  return request<{ success: boolean }>(
    session, 'admin-research-itineraries',
    { method: 'PATCH', body: { id, ...data } },
  )
}

export async function deactivateResearchItinerary(session: Session, id: string) {
  return request<{ success: boolean }>(
    session, 'admin-research-itineraries',
    { method: 'DELETE', body: { id } },
  )
}

// --- Research Detail ---

export interface PriceObservation {
  id: string
  price_usd: number
  observed_at: string
  departure_date: string
  cabin_class: string
  fare_family?: string
  stop_count?: number
  total_duration_minutes?: number
  is_roundtrip: boolean
}

export interface ResearchDrop {
  drop_amount: number
  drop_percent: number
  prev_price: number
  current_price: number
  observed_at: string
  days_before_departure: number
}

export interface ResearchItineraryDetail {
  success: boolean
  itinerary: ResearchItinerary
  observations: PriceObservation[]
  drops: ResearchDrop[]
}

export async function getResearchItineraryDetail(session: Session, itineraryId: string) {
  return request<ResearchItineraryDetail>(
    session, 'admin-get-research-itinerary-detail',
    { params: { itinerary_id: itineraryId } },
  )
}

// --- Users / Approve ---

export interface WaitlistUser {
  id: string
  email: string
  interests: string[]
  approved_at?: string | null
  created_at: string
}

export async function getWaitlistUsers(session: Session) {
  return request<{ success: boolean; users: WaitlistUser[] }>(
    session, 'admin-get-waitlist-users',
  )
}

export async function approveUser(session: Session, email: string) {
  return request<{ success: boolean }>(
    session, 'admin-approve-user',
    { method: 'POST', body: { email } },
  )
}

// --- User Explorer ---

export interface UserSearchResultUser {
  kind: 'user'
  user_id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  stripe_customer_id: string | null
  card_brand: string | null
  card_last4: string | null
  created_at: string
  matched_on: 'email' | 'first_name' | 'last_name' | 'stripe_customer_id' | 'pnr'
}

export interface UserSearchResultPreUser {
  kind: 'pre_user'
  email: string
  interests: string | null
  approved_at: string | null
  created_at: string
  matched_on: 'email'
}

export type UserSearchResult = UserSearchResultUser | UserSearchResultPreUser

export async function searchUsers(session: Session, query: string, signal?: AbortSignal) {
  return request<{ success: boolean; results: UserSearchResult[] }>(
    session,
    'admin-search-users',
    { params: { q: query }, signal },
  )
}

export interface UserHealthBadges {
  card: 'ok' | 'warning' | 'error' | 'muted'
  billing: 'ok' | 'warning' | 'error' | 'muted'
  bookings: 'ok' | 'warning' | 'error' | 'muted'
  issues: 'ok' | 'error' | 'muted'
}

export interface UserDetailProfile {
  user_id: string
  email: string | null
  first_name: string | null
  last_name: string | null
  stripe_customer_id: string | null
  card_brand: string | null
  card_last4: string | null
  card_exp_month: number | null
  card_exp_year: number | null
  email_notifications_enabled: boolean | null
  welcome_email_sent_at: string | null
  created_at: string
}

export interface UserDetailStats {
  active_bookings: number
  archived_bookings: number
  total_bookings: number
  commission_counts: Record<string, number>
  commission_lifetime_total: number
  lifetime_savings: number
  open_linear_issues: number
  latest_price_check: string | null
}

export interface UserDetailResponse {
  success: boolean
  kind: 'user'
  profile: UserDetailProfile
  waitlist: { email: string; interests: string | null; approved_at: string | null; created_at: string } | null
  stats: UserDetailStats
  health: UserHealthBadges
}

export interface PreUserDetailResponse {
  success: boolean
  kind: 'pre_user'
  pre_user: {
    id: string
    email: string
    interests: string | null
    approved_at: string | null
    created_at: string
  }
}

export async function getUserDetail(session: Session, userId: string) {
  return request<UserDetailResponse>(session, 'admin-get-user-detail', {
    params: { user_id: userId },
  })
}

export async function getPreUserDetail(session: Session, email: string) {
  return request<PreUserDetailResponse>(session, 'admin-get-user-detail', {
    params: { kind: 'pre_user', email },
  })
}

/** User-level events share the booking timeline event shape. */
export type UserActivityEvent = TimelineEvent

export interface UserActivityResponse {
  success: boolean
  events: UserActivityEvent[]
  next_cursor: TimelineCursor | null
}

export async function getUserActivity(
  session: Session,
  userId: string,
  cursor?: TimelineCursor | null,
) {
  const params: Record<string, string> = { user_id: userId }
  if (cursor) {
    params.cursor_ts = cursor.ts
    params.cursor_id = cursor.source_id
  }
  return request<UserActivityResponse>(session, 'admin-get-user-activity', { params })
}

export async function resendWelcomeEmail(session: Session, userId: string) {
  return request<{ success: boolean }>(session, 'admin-resend-email', {
    method: 'POST',
    body: { type: 'welcome', user_id: userId },
  })
}

export async function resendWaitlistApprovalEmail(session: Session, email: string) {
  return request<{ success: boolean }>(session, 'admin-resend-email', {
    method: 'POST',
    body: { type: 'waitlist_approval', email },
  })
}

export async function triggerPriceCheck(session: Session, bookingId: string) {
  return request<{ success: boolean; result?: unknown }>(session, 'admin-trigger-price-check', {
    method: 'POST',
    body: { booking_id: bookingId },
  })
}

export async function approveWaitlist(session: Session, email: string) {
  return request<{ success: boolean }>(session, 'admin-approve-waitlist', {
    method: 'POST',
    body: { email },
  })
}

// --- Email logs ---

import type { EmailLog, EmailLogCounts, EmailLogStatus } from './types'

export interface ReparseResult {
  success: boolean
  error?: string
  notificationAttempted?: boolean
  notificationType?: string
  notificationSent?: boolean
  notificationSkipped?: boolean
  notificationError?: string
  notificationReason?: string
}

export interface EmailLogsResponse {
  success: boolean
  logs: EmailLog[]
  counts: EmailLogCounts
  pagination?: { total: number }
}

export async function getEmailLogs(
  session: Session,
  params: { limit: number; offset: number; status?: EmailLogStatus | 'all'; search?: string },
) {
  return request<EmailLogsResponse>(session, 'admin-get-email-logs', {
    params: {
      limit: params.limit,
      offset: params.offset,
      status: params.status && params.status !== 'all' ? params.status : undefined,
      search: params.search?.trim() || undefined,
    },
  })
}

export async function reparseEmailLog(session: Session, logId: string, sendNotification: boolean) {
  return request<ReparseResult>(session, 'admin-reparse-email-log', {
    method: 'POST',
    body: { logId, sendNotification },
  })
}

// --- Email testing ---

export async function sendTestEmail(
  session: Session,
  params: { emailType: string; recipientEmail: string; params: unknown; preview?: boolean },
) {
  return request<{ success: boolean; emailId?: string; error?: string }>(session, 'admin-send-test-email', {
    method: 'POST',
    body: params,
  })
}

// --- Research Analytics ---

export interface ResearchAnalyticsData {
  success: boolean
  overview: {
    total_itineraries: number
    total_observations: number
    total_drops: number
    avg_drop_percent: number
    avg_drop_amount: number
  }
  itinerary_stats: Array<{
    itinerary_id: string
    label: string
    origin: string
    destination: string
    carrier_code: string
    flight_number: string
    trip_type: string
    departure_date: string
    latest_price: number | null
    latest_checked_at: string | null
    observation_count: number
    drop_count: number
    drop_rate: number
    avg_drop_amount: number
    avg_drop_percent: number
    max_drop_amount: number
    min_price: number | null
    avg_price: number | null
  }>
  carrier_comparison: Array<{
    carrier: string
    total_observations: number
    drop_count: number
    drop_frequency_pct: number
    avg_drop_amount: number
    avg_drop_percent: number
  }>
  drops_by_days_bucket: Array<{
    bucket: string
    total_observations: number
    drop_count: number
    drop_frequency_pct: number
    avg_drop_amount: number
    avg_drop_percent: number
  }>
  recent_drops: Array<{
    origin: string
    destination: string
    departure_date: string
    primary_carrier: string
    flight_numbers: string[]
    current_price: number
    prev_price: number
    drop_amount: number
    drop_percent: number
    days_before_departure: number
    observed_at: string
  }>
  price_trend: Array<{
    price_usd: number
    observed_at: string
    primary_carrier: string
    departure_date: string
  }> | null
}

export async function getResearchAnalytics(
  session: Session,
  route?: { origin?: string; destination?: string },
) {
  return request<ResearchAnalyticsData>(session, 'admin-get-research-analytics', {
    params: {
      route_origin: route?.origin,
      route_destination: route?.destination,
    },
  })
}
